import { Loader } from '@mantine/core';
import type { ImgHTMLAttributes } from 'react';
import { useEffect, useRef, useState } from 'react';
import { delay } from '~/common/time/delay';

/* oxlint-disable @next/next/no-img-element */
/* oxlint-disable jsx-a11y/alt-text */

const MAX_RETRIES = 20;
export default function RetryImage({
  fallbackSrc,
  loadingBgComponent,
  maxRetries,
  ...props
}: ImgHTMLAttributes<HTMLImageElement> & {
  onImageLoaded?: (img: HTMLImageElement) => void;
  fallbackSrc?: string;
  loadingBgComponent?: React.ReactNode;
  maxRetries?: number;
}) {
  const [imgUrl, setImgUrl] = useState(props.src);

  const imgRef = useRef<HTMLImageElement>(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);
  const [showLoadingSpinner, setShowLoadingSpinner] = useState(true);

  useEffect(() => {
    if (!retrying || !imgUrl || typeof imgUrl !== 'string') {
      return;
    }

    let retry = true;
    let retryCount = 0;
    (async () => {
      while (retry && retryCount < (maxRetries || MAX_RETRIES)) {
        // Go to fallback after 3 retries
        if (fallbackSrc && retryCount === 3 && imgUrl !== fallbackSrc) {
          setImgUrl(fallbackSrc);
        }
        await delay(Math.min(1000, retryCount * 100));
        // const res = await asyncStuff
        const imgReady = await fetch(imgUrl, { method: 'HEAD' })
          .catch(
            (_) =>
              ({
                ok: false,
              }) as Response,
          )
          .then((res) => res.ok);
        if (imgReady) {
          retry = false;
          // Set the img src
          if (imgRef.current) {
            imgRef.current.src = '';
            imgRef.current.src = imgUrl;
          }
        }
        retryCount++;
      }
      setShowLoadingSpinner(false);
      // TODO:
    })().catch(() => undefined);

    return () => {
      setShowLoadingSpinner(true);
      retry = false;
    };
  }, [props.src, fallbackSrc, retrying, imgUrl]);

  return (
    <>
      {loading ?
        <div className="relative flex h-full items-center justify-center">
          {loadingBgComponent}
          <div className="absolute inset-0 flex items-center justify-center">
            {showLoadingSpinner ?
              <Loader />
            : null}
          </div>
        </div>
      : null}
      <img
        className="w-full"
        {...props}
        src={imgUrl}
        ref={imgRef}
        onLoad={(e) => {
          setLoading(false);
          setRetrying(false);
          e.currentTarget.style.display = 'inline-block';
          if (props.onImageLoaded && imgRef.current) {
            props.onImageLoaded(imgRef.current);
          }
        }}
        onError={(e) => {
          e.currentTarget.style.display = 'none';
          setRetrying(true);
        }}
      />
    </>
  );
}
